import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import utils from '../../utils';
import { GrAdd, GrSearch } from 'react-icons/gr';
import { FiDownload, FiPlusCircle, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import FetchError from '../../components/feedback/FetchError';
import moment from 'moment';
import Pagination from '../../components/nav/Pagination';
import Empty from '../../components/feedback/Empty';
import Loading from '../../components/feedback/Loading';
import adminServices from '../../services/AdminServices';
import PaymentModal from '../../components/payments/PaymentModal';
import {
    MdAttachMoney,
    MdPendingActions,
    MdUpdate
} from 'react-icons/md';
import { FaMoneyBillTransfer } from 'react-icons/fa6';

function PaymentsListPage(props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payments, setPayments] = useState(null);
    const [action, setAction] = useState(0);
    const [activePayment, setActivePayment] = useState(null);
    const [stats, setStats] = useState(null);

    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentOrdering = searchParams.get('ordering') || '';
    const currentSearch = searchParams.get('search') || '';
    const currentPurpose = searchParams.get('purpose') || '';
    const currentStatus = searchParams.get('status') || '';

    useEffect(() => {
        const params = searchParams.toString();
        getPayments(params);
        fetchStats();
    }, [searchParams]);

    const fetchStats = async () => {
        try {
            const res = await adminServices.getPaymentStats();
            setStats(res.data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    const getPayments = (params) => {
        setError(null);
        setLoading(true);
        adminServices.getPayments(params ? `?${params}` : '').then(
            res => {
                setPayments(res.data);
                setLoading(false);
            },
            err => {
                console.error(err);
                setError(err);
                setLoading(false);
            }
        );
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page');
                return newParams;
            });
        }
    };

    const updateURL = (newParams) => {
        navigate({
            pathname: location.pathname,
            search: newParams.toString(),
        }, { replace: true });
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');
        updateURL(params);
    };

    const handlePurposeChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('purpose', value);
        } else {
            params.delete('purpose');
        }
        params.delete('page');
        updateURL(params);
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);
        if (value) {
            params.set('status', value);
        } else {
            params.delete('status');
        }
        params.delete('page');
        updateURL(params);
    };

    const changePageParam = (page) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    const downloadCsv = () => {
        toast.error("Feature coming soon");
    };

    const refreshData = () => {
        const params = searchParams.toString();
        getPayments(params ? `?${params}` : '');
        fetchStats();
        toast.success('Data refreshed');
    };

    function openModal(action, payment = null) {
        const openBtn = document.getElementById("payment-open-btn");
        setAction(action);
        setActivePayment(payment);
        if (openBtn) openBtn.click();
    }

    const getStatusBadge = (status) => {
        const styles = {
            Pending: 'badge-warning',
            Paid: 'badge-success',
            Failed: 'badge-error',
            Refunded: 'badge-ghost',
        };
        return styles[status] || 'badge-ghost';
    };

    const getPurposeBadge = (purpose) => {
        return purpose === 'Service Payment' ? 'badge-info' : 'badge-accent';
    };

    const getDisbursementBadge = (status) => {
        const styles = {
            Pending: 'badge-warning',
            Processing: 'badge-info',
            Paid: 'badge-success',
            Failed: 'badge-error',
        };
        return styles[status] || 'badge-ghost';
    };

    // const utils.toCommas = (amount) => {
    //     return amount ? `KES ${parseFloat(amount).toLocaleString('en-KE')}` : '—';
    // };

    const url = window.location.href;

    return (
        <div className="mb-16">
            <Helmet>
                <title>Payments - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta property="description" content="Manage payments, transactions, and nurse disbursements." />
            </Helmet>

            <PaymentModal
                onReload={getPayments}
                setActivePayment={setActivePayment}
                action={action}
                activePayment={activePayment}
            />

            <div className="container mx-auto space-y-5">
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {/* Total Revenue */}
                        <div className="relative overflow-hidden rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-4 md:p-6 shadow-sm transition-all hover:shadow-md">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs md:text-sm font-medium text-base-content/70">Total Revenue</p>
                                    <p className="mt-2 text-2xl md:text-3xl font-bold text-neutral">
                                        {utils.toCommas(stats.total_revenue)}
                                    </p>
                                </div>
                                <div className="rounded-lg bg-primary/10 text-primary p-2 md:p-3">
                                    <FaMoneyBillTransfer className="h-5 w-5 md:h-6 md:w-6" />
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-hidden rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-4 md:p-6 shadow-sm transition-all hover:shadow-md">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs md:text-sm font-medium text-base-content/70">Total Disbursed</p>
                                    <p className="mt-2 text-2xl md:text-3xl font-bold text-neutral">
                                        {utils.toCommas(stats.total_disbursed)}
                                    </p>
                                </div>
                                <div className="rounded-lg bg-primary/10 text-primary p-2 md:p-3">
                                    <MdAttachMoney className="h-5 w-5 md:h-6 md:w-6" />
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-hidden rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-4 md:p-6 shadow-sm transition-all hover:shadow-md">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs md:text-sm font-medium text-base-content/70">Pending Disbursements</p>
                                    <p className="mt-2 text-2xl md:text-3xl font-bold text-neutral">
                                        {stats.pending_disbursements || 0}
                                    </p>
                                    <p className="text-xs text-base-content/70 hidden md:block mt-1">
                                        {utils.toCommas(stats.pending_amount || '')}
                                    </p>
                                </div>
                                <div className="rounded-lg bg-primary/10 text-primary p-2 md:p-3">
                                    <MdPendingActions className="h-5 w-5 md:h-6 md:w-6" />
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-hidden rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-4 md:p-6 shadow-sm transition-all hover:shadow-md">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs md:text-sm font-medium text-base-content/70">Last 30 Days</p>
                                    <p className="mt-2 text-2xl md:text-3xl font-bold text-neutral">
                                        {utils.toCommas(stats.last_30_days_revenue)}
                                    </p>
                                </div>
                                <div className="rounded-lg bg-primary/10 text-primary p-2 md:p-3">
                                    <MdUpdate className="h-5 w-5 md:h-6 md:w-6" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Payments Management</h1>
                        <p className="text-zinc-500 mt-1">Track and manage all patient payments and transactions.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={refreshData}
                            className="items-center justify-center px-3 py-2 bg-white dark:bg-zinc-800 border border-border-light dark:border-zinc-700 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                            title="Refresh data"
                        >
                            <FiRefreshCw className={`inline text-[18px] ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={downloadCsv}
                            className="items-center hidden md:inline-flex justify-center px-4 py-2 bg-white dark:bg-zinc-800 border border-border-light dark:border-zinc-700 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                        >
                            <FiDownload className="inline mr-2 text-[20px]" />
                            Export
                        </button>
                        <button
                            onClick={() => openModal(0)}
                            className="inline-flex items-center justify-center px-4 py-2 bg-primary hover:bg-sky-600 border border-transparent rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                        >
                            <GrAdd className="inline mr-2 text-[20px]" />
                            New Payment
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-surface-light p-4 rounded-xl border border-border-light">
                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                        <div className="">
                            <label className="sr-only" htmlFor="payment-search">Search</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <GrSearch className="text-zinc-400 text-[20px]" />
                                </div>
                                <input
                                    className="block w-full pl-10 pr-3 py-2.5 border border-border-light rounded-lg bg-zinc-50 text-zinc-900 placeholder-zinc-500 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                                    id="payment-search"
                                    name="payment-search"
                                    placeholder="Search by name, email, reference or license"
                                    type="text"
                                    defaultValue={currentSearch}
                                    onKeyDown={handleSearch}
                                    ref={searchInputRef}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row flex-wrap gap-3">
                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentPurpose}
                                    onChange={handlePurposeChange}
                                >
                                    <option value="">All Purposes</option>
                                    <option value="Service Payment">Service Payment</option>
                                    <option value="Nurse Disbursement">Nurse Disbursement</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>

                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentStatus}
                                    onChange={handleStatusChange}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Failed">Failed</option>
                                    <option value="Refunded">Refunded</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>

                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentOrdering}
                                    onChange={handleOrderingChange}
                                >
                                    <option value="">Sort By</option>
                                    <option value="-creation_time">Newest First</option>
                                    <option value="creation_time">Oldest First</option>
                                    <option value="-total">Amount (High-Low)</option>
                                    <option value="total">Amount (Low-High)</option>
                                    <option value="-disbursed_at">Disbursed (Newest)</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <FetchError error={error} />}

                {loading ? (
                    <Loading />
                ) : (
                    <div>
                        <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="table table-compact table-zebra w-full">
                                    <thead>
                                        <tr>
                                            <th>Payment</th>
                                            <th className="hidden md:table-cell">Purpose</th>
                                            <th className="hidden md:table-cell">Status</th>
                                            <th>Amount</th>
                                            <th className="hidden lg:table-cell">Method</th>
                                            <th className="hidden lg:table-cell">Nurse Amount</th>
                                            <th className="hidden xl:table-cell">Platform Amount</th>
                                            <th className="hidden md:table-cell">Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments?.results?.length > 0 ? (
                                            payments.results.map((payment) => (
                                                <tr key={payment.id} className="hover">
                                                    <td>
                                                        <div className="flex flex-col">
                                                            <span className="font-mono text-xs text-zinc-500">
                                                                #{payment.id}
                                                            </span>
                                                            <span className="font-medium text-sm">
                                                                {payment.purpose === 'Service Payment'
                                                                    ? payment.owner?.user?.full_name || '—'
                                                                    : payment.nurse?.user?.full_name || '—'}
                                                            </span>
                                                            <span className="text-xs text-zinc-500 truncate max-w-32 md:max-w-none">
                                                                {payment.transaction_reference || payment.transaction_id || '—'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        <div className={`badge ${getPurposeBadge(payment.purpose)} badge-sm`}>
                                                            {payment.purpose}
                                                        </div>
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        <div className={`badge ${getStatusBadge(payment.status)} badge-sm`}>
                                                            {payment.status}
                                                        </div>
                                                        {payment.purpose === 'Nurse Disbursement' && (
                                                            <div className={`badge ${getDisbursementBadge(payment.disbursement_status)} badge-xs mt-1`}>
                                                                {payment.disbursement_status}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="font-semibold text-sm">
                                                            {utils.toCommas(payment.total)}
                                                        </span>
                                                    </td>
                                                    <td className="hidden lg:table-cell">
                                                        <span className="text-sm text-zinc-600">
                                                            {payment.payment_method}
                                                        </span>
                                                    </td>
                                                    <td className="hidden lg:table-cell">
                                                        <span className="text-sm text-success font-medium">
                                                            {utils.toCommas(payment.nurse_amount)}
                                                        </span>
                                                    </td>
                                                    <td className="hidden xl:table-cell">
                                                        <span className="text-sm text-zinc-600">
                                                            {utils.toCommas(payment.platform_amount)}
                                                        </span>
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        <span className="text-sm text-zinc-600">
                                                            {moment(payment.creation_time).format("MMM Do")}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => openModal(1, payment)}
                                                            className="btn btn-secondary btn-sm"
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : null}
                                    </tbody>
                                </table>
                                {payments?.results?.length === 0 && (
                                    <div className='p-3'>
                                        <Empty objectName="Payments" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {!loading && !error && payments && payments.count > 0 && (
                            <div className="flex justify-center mt-7">
                                <Pagination
                                    count={payments.count}
                                    next={payments.next}
                                    previous={payments.previous}
                                    changePageParam={changePageParam}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PaymentsListPage;